import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { AlertService } from '../components/alert-service/alert-service';

export const DEFAULT_MAX_SIMULATION_FILES = 50;
export const MAX_SIMULATION_FILES_LIMIT = 500;

export interface UserPreferences {
  show_advanced_options: boolean;
  default_delfos_server_config_id?: string;
  default_delfos_llm_config_id?: string;
  default_di2winocr_server_config_id?: string;
  default_di2winocr_features_config_id?: string;
  ai_model_language: 'pt' | 'en' | 'es';
  clear_delfos_cache: boolean;
  clear_di2winocr_cache: boolean;
  use_multimodal: boolean;
  tutorial_seen?: boolean;
  theme: 'light' | 'dark';
  ui_language: 'pt' | 'en' | 'es';
  max_simulation_files: number;
}

export const DEFAULT_USER_PREFERENCES: UserPreferences = {
  show_advanced_options: false,
  default_delfos_server_config_id: '',
  default_delfos_llm_config_id: '',
  default_di2winocr_server_config_id: '',
  default_di2winocr_features_config_id: '',
  ai_model_language: 'pt',
  clear_delfos_cache: false,
  clear_di2winocr_cache: false,
  use_multimodal: false,
  tutorial_seen: false,
  theme: 'light',
  ui_language: 'pt',
  max_simulation_files: DEFAULT_MAX_SIMULATION_FILES
};

export interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  provider: 'local' | 'dilbert' | string;
  created_at?: string;
  updated_at?: string;
  last_login?: string;
  is_active?: boolean;
  preferences?: UserPreferences;
}

export interface CreateUserRequest {
  username: string;
  email: string;
  role: string;
  password: string;
}

export interface UpdateUserRequest {
  username: string;
  email: string;
  role: string;
  password: string;
}

export type UserRole =
  | 'EXTERNAL_USER'
  | 'INTERNAL_COLABORATOR'
  | 'ADMIN'
  | 'INTERNAL_DEVELOPER';

@Injectable({ providedIn: 'root' })
export class UsersService {
  private baseUrl = '/api/users';

  constructor(private http: HttpClient, private alertService: AlertService) {}

  list(): Observable<{ users: User[] }> {
    return this.http.get<{ users: User[] }>(`${this.baseUrl}/`);
  }

  getMe(): Observable<{ user: User }> {
    return this.http.get<{ user: User }>(`${this.baseUrl}/me`);
  }

  updatePreferences(preferences: UserPreferences): Observable<{ preferences: UserPreferences }> {
    return this.http.put<{ preferences: UserPreferences }>(`${this.baseUrl}/preferences`, preferences);
  }

  listUsernames(): Observable<{ usernames: string[] }> {
    return this.list().pipe(
      map(response => ({
        usernames: response.users.map(user => user.username)
      }))
    );
  }

  create(data: CreateUserRequest): Observable<{ user: User }> {
    return this.http.post<{ user: User }>(`${this.baseUrl}/`, data);
  }

  get(id: string): Observable<{ user: User }> {
    return this.http.get<{ user: User }>(`${this.baseUrl}/${id}`);
  }

  update(id: string, data: UpdateUserRequest): Observable<{ user: User }> {
    return this.http.put<{ user: User }>(`${this.baseUrl}/${id}`, data);
  }

  updateRole(id: string, role: UserRole): Observable<{ user: User }> {
    return this.http.put<{ user: User }>(`${this.baseUrl}/role/${id}?new_role=${role}`, {});
  }

  delete(id: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/${id}`);
  }

  listWithFilters(role?: string, isActive?: boolean): Observable<{ users: User[] }> {
    let params = new HttpParams();
    if (role) params = params.set('role', role);
    if (isActive !== undefined) params = params.set('is_active', isActive.toString());
    return this.http.get<{ users: User[] }>(`${this.baseUrl}`, { params });
  }

  validateUserData(userData: CreateUserRequest | UpdateUserRequest, _excludeId?: string): string[] {
    const errors: string[] = [];

    if (!userData.username || userData.username.trim().length === 0) {
      errors.push('Nome de usuário é obrigatório');
    } else if (userData.username.trim().length < 3) {
      errors.push('Nome de usuário deve ter pelo menos 3 caracteres');
    } else if (userData.username.trim().length > 50) {
      errors.push('Nome de usuário não pode exceder 50 caracteres');
    }

    if (!userData.email || userData.email.trim().length === 0) {
      errors.push('E-mail é obrigatório');
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userData.email)) {
        errors.push('E-mail deve ter um formato válido');
      }
    }

    const validRoles: UserRole[] = [
      'ADMIN',
      'EXTERNAL_USER',
      'INTERNAL_COLABORATOR',
      'INTERNAL_DEVELOPER'
    ];
    if (!userData.role || !validRoles.includes(userData.role as UserRole)) {
      errors.push('Função deve ser uma das opções: ADMIN, EXTERNAL_USER, INTERNAL_COLABORATOR, INTERNAL_DEVELOPER');
    }

    if (!('password' in userData) || !userData.password || userData.password.trim().length === 0) {
      errors.push('Senha é obrigatória');
    } else if (userData.password.trim().length < 6) {
      errors.push('Senha deve ter pelo menos 6 caracteres');
    }

    return errors;
  }

  getAvailableRoles(): { value: UserRole; labelKey: string }[] {
    return [
      { value: 'EXTERNAL_USER',        labelKey: 'USERS.EXTERNAL_USER' },
      { value: 'INTERNAL_COLABORATOR', labelKey: 'USERS.INTERNAL_COLABORATOR' },
      { value: 'INTERNAL_DEVELOPER',   labelKey: 'USERS.INTERNAL_DEVELOPER' },
      { value: 'ADMIN',                labelKey: 'USERS.ADMIN' }
    ];
  }

  listUserConfigurations(userId: string): Observable<{ configurations: any[] }> {
    return this.http.get<{ configurations: any[] }>(`${this.baseUrl}/${userId}/configurations`);
  }

  addConfigurationToUser(userId: string, configId: string): Observable<any> {
    return this.http.post<any>(`${this.baseUrl}/${userId}/configurations/${configId}`, {});
  }

  removeConfigurationFromUser(userId: string, configId: string): Observable<any> {
    return this.http.delete<any>(`${this.baseUrl}/${userId}/configurations/${configId}`);
  }
}